import type { SpaceHue } from "@/lib/projectColour";

/**
 * The flat shell (design demo, 2026-11-02): the menu, breadcrumb and header
 * icons sit on one flat ground tinted by the project's colour, and only the
 * page is raised, as a card. Arc's layout, SaleFish's own parts.
 *
 * On by default in this demo; `?shell=classic` switches back to today's shell
 * for comparison (remembered per device), `?shell=flat` returns.
 */

const KEY = "salefish:shell:v1";

export function isFlatShell(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const asked = new URLSearchParams(window.location.search).get("shell");
    if (asked === "classic" || asked === "flat") window.localStorage.setItem(KEY, asked);
    return window.localStorage.getItem(KEY) !== "classic";
  } catch {
    return true;
  }
}

/** Opens the command palette from anywhere: the header's search icon, the menu's field. */
export const OPEN_COMMAND_EVENT = "salefish:open-command";
export function openCommand(): void {
  window.dispatchEvent(new Event(OPEN_COMMAND_EVENT));
}

/** Opens Livvy from the palette: with a question to ask, or listening. */
export const ASK_LIVVY_EVENT = "salefish:ask-livvy";
export function askLivvy(question: string, opts: { listen?: boolean } = {}): void {
  window.dispatchEvent(new CustomEvent(ASK_LIVVY_EVENT, { detail: { question, listen: !!opts.listen } }));
}

/** Words that make the field read as a question for Livvy rather than a name to find. */
export function readsAsQuestion(text: string): boolean {
  const s = text.trim().toLowerCase();
  if (!s) return false;
  if (s.endsWith("?")) return true;
  if (s.split(/\s+/).length >= 4) return true;
  return /^(what|who|whose|which|when|where|why|how|show|find|list|give|put|add|create|make|mark|move|log|set|change|remind|tell|is|are|do|does|can|did)\b/.test(s);
}

/*
 * A project's colour, as a hue. There is no project colour setting yet — in
 * the real thing it is picked in Project Settings (drawn from the logo by
 * default). For the demo each project takes one of six hues from its id, so
 * switching spaces visibly changes the room.
 */
const HUES = [256, 158, 212, 18, 186, 322];
export function spaceHue(projectId: string | null | undefined): number {
  if (!projectId) return HUES[0];
  let h = 0;
  for (let i = 0; i < projectId.length; i++) h = (h * 31 + projectId.charCodeAt(i)) >>> 0;
  return HUES[h % HUES.length];
}

/** Whether the reader hid the menu (⌘\\), per device. */
const MENU_HIDDEN_KEY = "salefish:menu-hidden:v1";
export function readMenuHidden(): boolean {
  try {
    return window.localStorage.getItem(MENU_HIDDEN_KEY) === "1";
  } catch {
    return false;
  }
}
export function writeMenuHidden(hidden: boolean): void {
  try {
    window.localStorage.setItem(MENU_HIDDEN_KEY, hidden ? "1" : "0");
  } catch {
    /* private mode: the menu still hides, it just is not remembered */
  }
}

/** The shortcut to the nth space: ⌃1 on Apple, Alt+1 elsewhere (Ctrl+1 is the browser's tab key there). */
export function spaceShortcut(n: number): string {
  return isApplePlatform() ? `⌃${n}` : `Alt+${n}`;
}
/** Hides or shows the menu from anywhere (the ⌘K palette). */
export const TOGGLE_MENU_EVENT = "salefish:toggle-menu";

/** The menu shortcut's label. */
export function menuShortcut(): string {
  return isApplePlatform() ? "⌘\\" : "Ctrl+\\";
}

/** True when the keyboard shortcut should read ⌘ rather than Ctrl. */
export function isApplePlatform(): boolean {
  if (typeof navigator === "undefined") return false;
  return /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent);
}


/** The last hue each project's ground was drawn in, per device, so a cold
 *  launch opens in the project's own colour rather than the default and then
 *  shifting once the project and its logo have loaded. */
const SPACE_HUE_KEY = "salefish:space-hue:v1";
export function rememberSpaceHue(projectId: string, hue: SpaceHue): void {
  try {
    const all = JSON.parse(window.localStorage.getItem(SPACE_HUE_KEY) ?? "{}") as Record<string, SpaceHue>;
    if (all[projectId] === hue) return;
    all[projectId] = hue;
    window.localStorage.setItem(SPACE_HUE_KEY, JSON.stringify(all));
  } catch {
    /* private mode: the default hue until the project loads */
  }
}
export function rememberedSpaceHue(projectId: string): SpaceHue | null {
  try {
    const hue = (JSON.parse(window.localStorage.getItem(SPACE_HUE_KEY) ?? "{}") as Record<string, unknown>)[projectId];
    return typeof hue === "number" || hue === "grey" ? hue : null;
  } catch {
    return null;
  }
}

/**
 * Draws the room in a hue, or in greyscale: `--space-s` scales every
 * saturation the ground, tab bar and glass take from the project to zero,
 * and `space-grey` neutralises the dark theme's purple base (index.css).
 */
export function applySpaceHue(root: HTMLElement, hue: SpaceHue): void {
  const grey = hue === "grey";
  root.style.setProperty("--space-h", String(grey ? 0 : hue));
  root.style.setProperty("--space-s", grey ? "0" : "1");
  root.classList.toggle("space-grey", grey);
}

/**
 * The flat shell's classes on <html> before React mounts, so the cold
 * launch's frame (ui/route-skeleton ShellSkeleton) is drawn on the flat
 * ground, with the menu hidden if it was, in the project's last colour.
 * AppShell takes them over once it mounts.
 */
export function applyShellAtBoot(path: string): void {
  if (!isFlatShell()) return;
  const root = document.documentElement;
  root.classList.add("shell-flat");
  if (readMenuHidden()) root.classList.add("menu-hidden");
  const projectId = /^\/projects\/([^/]+)/.exec(path)?.[1];
  const hue = projectId ? rememberedSpaceHue(projectId) : null;
  if (hue !== null) applySpaceHue(root, hue);
}

/**
 * Whether a hardware keyboard is attached, published as `has-keyboard` on
 * <html> so the ⌘K hint beside search shows on an iPad with one (Andrew,
 * 2026-11-02). No browser reports it, so it is inferred: a key pressed while
 * no text field has focus can only come from a hardware keyboard (the
 * on-screen one types into fields), and the on-screen keyboard rising — the
 * visual viewport losing a keyboard's height while a field has focus — means
 * there is none (it was detached).
 *
 * What is learned is REMEMBERED, and applied at the next page load (every
 * sign-in and every launch is one), never live: the hint appearing on the
 * first key press widened the search button 36px and slid the breadcrumb
 * mid-task (shift-audit chrome-close, 2026-11-02), and nothing in the chrome
 * may move while the reader works.
 */
const KEYBOARD_KEY = "salefish:has-keyboard:v2";
const isEditable = (el: Element | null) =>
  !!el && (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement || (el as HTMLElement).isContentEditable);
let watchingKeyboard = false;
export function watchHardwareKeyboard(): void {
  if (watchingKeyboard || typeof window === "undefined") return;
  watchingKeyboard = true;
  const root = document.documentElement;
  let known: string | null = null;
  try {
    known = window.localStorage.getItem(KEYBOARD_KEY);
  } catch {
    /* nothing remembered */
  }
  if (known === "1") root.classList.add("has-keyboard");
  const remember = (on: boolean) => {
    if (known === (on ? "1" : "0")) return;
    known = on ? "1" : "0";
    try {
      window.localStorage.setItem(KEYBOARD_KEY, known);
    } catch {
      /* private mode: learned again next time */
    }
  };
  window.addEventListener(
    "keydown",
    (e) => {
      if (e.metaKey || e.ctrlKey || !isEditable(document.activeElement)) remember(true);
    },
    { capture: true },
  );
  const vv = window.visualViewport;
  if (vv) {
    let tallest = vv.height;
    vv.addEventListener("resize", () => {
      tallest = Math.max(tallest, vv.height);
      if (isEditable(document.activeElement) && tallest - vv.height > 150) remember(false);
    });
  }
}
